<template>
  <div>
    <activities-weekly-table
      v-if="isActivityProgressMode"
      activities-load-mode="project"
      summary-badge-key="global_view"
      :project-id="projectId"
      :week-start-date="weekStartDate"
      :week-end-date="weekEndDate"
      :week-number="weekNumber"
      :filter-subactivities-by-week="filterSubactivitiesByWeek"
      :reschedule-mode="rescheduleMode"
      :edited-subactivities="editedSubactivities"
      :activity-filters="activityFilters"
      @update-rescheduled-activity="$emit('update-rescheduled-activity', $event)"
      @remove-rescheduled-activity="$emit('remove-rescheduled-activity', $event)"
    />

    <div
      v-else
      class="weekly-view-global w-full max-w-full min-w-0 bg-white border rounded-lg overflow-hidden"
      style="border-color: #5b80be;"
    >
    <div
      class="flex flex-wrap items-center justify-between gap-2 py-2 px-3 text-sm border-b"
      style="background-color: #eef3fb; border-color: #c5d4eb;"
    >
      <div class="flex items-center gap-2 font-semibold" style="color: #3b66bc;">
        <b-icon pack="fas" icon="globe" size="is-small" />
        <span
          class="inline-flex items-center py-px px-1.5 rounded-sm text-white font-bold uppercase"
          style="background-color: #3b66bc; font-size: 10px;"
        >{{ $t('global_view') }}</span>
        <span>{{ summaryLabel }}</span>
      </div>
      <!-- <button type="button" class="weekly-view-global__add-btn">
        <b-icon pack="fas" icon="plus" size="is-small" />
        <span>{{ $t('add_subactivity') }}</span>
      </button> -->
    </div>

    <div v-if="isLoading" class="py-8 text-center text-sm text-gray-500">
      {{ $t('loading') }}...
    </div>

    <template v-else>
      <div class="weekly-view-global__scroll relative w-full max-w-full min-w-0 overflow-x-auto">
        <table class="weekly-view-global__table w-full text-xs border-collapse min-w-max">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                class="weekly-view-global__th"
                :class="{ 'weekly-view-global__th--left': column.align === 'left' }"
              >
                {{ $t(column.labelKey) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!rows.length">
              <td :colspan="columns.length" class="weekly-view-global__empty">
                {{ $t('no_results') }}
              </td>
            </tr>
            <tr
              v-for="row in rows"
              :key="row.id"
              class="weekly-view-global__row"
            >
              <td class="weekly-view-global__td text-center whitespace-nowrap">
                <a
                  v-if="row.parentActivityId"
                  href="#"
                  class="weekly-view-global__link"
                  @click.prevent="goToActivity(row)"
                >
                  {{ row.parentIdLabel }}
                </a>
                <span v-else>{{ row.parentIdLabel }}</span>
              </td>
              <td class="weekly-view-global__td weekly-view-global__td--activity">
                <div class="weekly-view-global__activity-cell">
                  <span class="weekly-view-global__activity-name">{{ row.parentName || '—' }}</span>
                  <span
                    v-if="row.attachmentsCount"
                    class="weekly-view-global__attachment-badge"
                  >
                    <b-icon pack="fas" icon="paperclip" size="is-small" />
                    {{ row.attachmentsCount }}
                  </span>
                </div>
              </td>
              <td class="weekly-view-global__td text-center whitespace-nowrap">{{ row.code || '—' }}</td>
              <td class="weekly-view-global__td text-center whitespace-nowrap">—</td>
              <!-- subactividad -->
              <td class="weekly-view-global__td weekly-view-global__td--subactivity">
                <div class="weekly-view-global__subactivity-cell">
                  <span class="weekly-view-global__subactivity-name">{{ row.name || '—' }}</span>
                  <activity-status-label
                    v-if="row.status != null"
                    :status="row.status"
                    font-size="0.55"
                  />
                </div>
              </td>
              <!-- peso -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">
                <span v-if="row.weight != null">{{ row.weight }}%</span>
                <span v-else>—</span>
              </td>
              <!-- etiquetas -->
              <td class="weekly-view-global__td text-center">
                <span
                  v-for="tag in row.tagLabels"
                  :key="tag.key"
                  class="weekly-view-global__tag"
                  :style="{ backgroundColor: tag.color }"
                >
                  {{ tag.name }}
                </span>
                <span v-if="!row.tagLabels.length">—</span>
              </td>
              <!-- avance previo -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">
                <span v-if="row.prevWeekProgress != null">{{ row.prevWeekProgress }}%</span>
                <span v-else>—</span>
              </td>
              <!-- avance semanal (progress)-->
              <td class="weekly-view-global__td weekly-view-global__td--progress">
                <progress-bar
                  :step="parseFloat(row.weeklyProgress) || 0"
                  color="blue-light"
                  class="w-full"
                />
              </td>
              <!-- avance planificado: Inicio planificado -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">{{ row.plannedStart || '—' }}</td>
              <!-- avance planificado: Fin planificado -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">{{ row.plannedEnd || '—' }}</td>
              <!-- cantidad -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">
                <span v-if="row.quantity != null">{{ formatQuantity(row.quantity) }}</span>
                <span v-else>—</span>
              </td>
              <!-- unidad -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">{{ row.unit || '—' }}</td>
              <!-- programado -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">
                <span v-if="row.scheduledAssignment != null">{{ formatQuantity(row.scheduledAssignment) }}</span>
                <span v-else>—</span>
              </td>
              <!-- ejecutado -->
              <td class="weekly-view-global__td text-center whitespace-nowrap">
                <span
                  v-if="row.executedAssignment != null"
                  class="weekly-view-global__executed-value"
                >
                  {{ formatQuantity(row.executedAssignment) }}
                </span>
                <span v-else>—</span>
              </td>
              <!-- supervisor -->
              <td class="weekly-view-global__td whitespace-nowrap">{{ row.supervisor || '—' }}</td>
              <!-- manager -->
              <td class="weekly-view-global__td whitespace-nowrap">{{ row.manager || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="total > perPage"
        class="flex flex-wrap items-center justify-between gap-2 py-2 px-3 border-t"
        style="border-color: #c5d4eb;"
      >
        <span class="text-xs text-gray-600">{{ $t('count_results') }}: {{ total }}</span>
        <b-pagination
          :total="total"
          :current="page"
          :per-page="perPage"
          size="is-small"
          order="is-centered"
          pack="fas"
          icon-pack="fas"
          icon="chevron-left"
          icon-right="chevron-right"
          @change="onPageChange"
        />
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import ActivityStatusLabel from '·/components/activity-status-label.vue'
import ActivitiesWeeklyTable from './activities-weekly-table.vue'

const TABLE_COLUMNS = [
  { key: 'parent_id', labelKey: 'activity_id', align: 'center' },
  { key: 'parent_name', labelKey: 'activity', align: 'left' },
  { key: 'code', labelKey: 'code', align: 'center' },
  { key: 'kks', labelKey: 'kks', align: 'center' },
  { key: 'subactivity', labelKey: 'subactivity', align: 'left' },
  { key: 'weight', labelKey: 'weight', align: 'center' },
  { key: 'tag', labelKey: 'tags', align: 'center' },
  { key: 'prev_week_progress', labelKey: 'prev_week_progress', align: 'center' },
  { key: 'weekly_progress', labelKey: 'weekly_progress', align: 'center' },
  { key: 'planned_start', labelKey: 'planned_start', align: 'center' },
  { key: 'planned_end', labelKey: 'planned_end', align: 'center' },
  { key: 'quantity', labelKey: 'quantity', align: 'center' },
  { key: 'unit', labelKey: 'measurement_unit_short', align: 'center' },
  { key: 'scheduled_assignment', labelKey: 'scheduled_assignment', align: 'center' },
  { key: 'executed_assignment', labelKey: 'executed_assignment', align: 'center' },
  { key: 'supervisor', labelKey: 'supervisor', align: 'left' },
  { key: 'manager', labelKey: 'manager', align: 'left' },
]

const TAG_COLORS = ['#3b82f6', '#ea580c', '#166534', '#7c3aed', '#dc2626']

function mapSubactivityRow(item) {
  const parent = item.parent || {}
  const manager = item.manager && item.manager.fullname ? item.manager.fullname : null
  const tagLabels = buildTagLabels(item)

  return {
    id: item.id,
    parentActivityId: item.parent_id || (parent.id != null ? parent.id : null),
    parentIdLabel: parentActivityIdLabel(parent),
    parentName: parent.name || null,
    code: item.planning_code || null,
    name: item.name || null,
    status: item.status,
    weight: item.weight,
    tagLabels,
    prevWeekProgress: item.prev_week_progress != null ? item.prev_week_progress : null,
    weeklyProgress: item.progress != null ? item.progress : null,
    plannedStart: item.current_start_date || null,
    plannedEnd: item.current_end_date || null,
    quantity: item.primary_physical_quantity != null ? item.primary_physical_quantity : null,
    unit: item.primary_physical_unit || null,
    scheduledAssignment: item.scheduled_assignment_quantity != null
      ? item.scheduled_assignment_quantity
      : null,
    executedAssignment: item.executed_assignment_quantity != null
      ? item.executed_assignment_quantity
      : null,
    supervisor: item.manager && item.manager.department ? item.manager.department : null,
    manager,
    attachmentsCount: item.notifications && item.notifications.documents
      ? item.notifications.documents
      : 0,
  }
}

function parentActivityIdLabel(parent) {
  if (!parent) {
    return '—'
  }
  if (parent.planning_code) {
    return parent.planning_code
  }
  if (parent.id != null) {
    return `ACT-${String(parent.id).padStart(3, '0')}`
  }
  return '—'
}

function buildTagLabels(item) {
  if (Array.isArray(item.tags) && item.tags.length) {
    return item.tags.map((tag, index) => ({
      key: tag.id != null ? tag.id : `${tag.name}-${index}`,
      name: tag.name || tag,
      color: tag.color || TAG_COLORS[index % TAG_COLORS.length],
    }))
  }
  if (item.activityType && item.activityType.name) {
    return [{
      key: 'activity-type',
      name: item.activityType.name,
      color: TAG_COLORS[0],
    }]
  }
  return []
}

export default {
  name: 'weekly-view-global',

  components: {
    ActivityStatusLabel,
    ActivitiesWeeklyTable,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    filterSubactivitiesByWeek: {
      type: Boolean,
      default: true,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    editedSubactivities: {
      type: Array,
      default: () => [],
    },
    activityFilters: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isLoading: false,
      rows: [],
      page: 1,
      perPage: 25,
      total: 0,
      columns: TABLE_COLUMNS,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    isActivityProgressMode() {
      const configs = this.project && this.project.configs
      return !!(configs
        && configs.progress_type === 'weekly'
        && (configs.progress_type_mode === 'activity' || configs.progress_type_mode === 'takeoff'))
    },

    summaryLabel() {
      return this.$t('subactivities_in_project', { count: this.total })
    },
  },

  watch: {
    effectiveProjectId(projectId, previousProjectId) {
      if (projectId && projectId !== previousProjectId) {
        this.loadSubactivities(1)
      }
    },
    weekStartDate() {
      this.loadSubactivities(1)
    },
    weekEndDate() {
      this.loadSubactivities(1)
    },
    filterSubactivitiesByWeek() {
      this.loadSubactivities(1)
    },
    activityFilters: {
      deep: true,
      handler() {
        this.loadSubactivities(1)
      },
    },
  },

  created() {
    this.loadSubactivities(1)
  },

  methods: {
    buildRequestParams(page) {
      const projectId = this.effectiveProjectId
      if (!projectId) {
        return null
      }

      const params = {
        ...this.activityFilters,
        p: projectId,
        filter_subactivities: 1,
        include_parents_for_accessible_children: 1,
        with_boq_root: 1,
        with_location: 1,
        with_prelations: 1,
        with_fullpath: 1,
        order_field: 'planning_code',
        order_dir: 'asc',
        per_page: this.perPage,
        page,
      }

      if (this.filterSubactivitiesByWeek && this.weekStartDate && this.weekEndDate) {
        params.weekly_start = this.weekStartDate
        params.weekly_end = this.weekEndDate
      }

      return params
    },

    async loadSubactivities(page = 1) {
      const params = this.buildRequestParams(page)
      if (!params) {
        this.rows = []
        this.total = 0
        this.isLoading = false
        return
      }

      this.isLoading = true
      this.page = page

      try {
        const { data } = await axios.get('/api/v2/activities', { params })

        if (!data || !data.success || !Array.isArray(data.activities)) {
          this.rows = []
          this.total = 0
          this.$notify.error('error_loading_activities')
          return
        }

        this.rows = data.activities.map((item) => mapSubactivityRow(item))
        this.total = data.total != null ? data.total : this.rows.length
      } catch (error) {
        this.rows = []
        this.total = 0
        this.$notify.error('error_loading_activities')
      } finally {
        this.isLoading = false
      }
    },

    onPageChange(page) {
      this.loadSubactivities(page)
    },

    formatQuantity(value) {
      const number = Number(value)
      if (Number.isNaN(number)) {
        return value
      }
      return number.toFixed(2)
    },

    goToActivity(row) {
      if (!row.parentActivityId) {
        return
      }
      this.$router.push({
        name: 'activity.home',
        params: { activity_id: row.parentActivityId },
      })
    },
  },
}
</script>

<style scoped>
.weekly-view-global__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.875rem;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 0.25rem;
  background: #3b66bc;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-view-global__add-btn:hover {
  opacity: 0.92;
}

.weekly-view-global__th {
  padding: 0.5rem 0.625rem;
  border: 1px solid #4a6fa5;
  background: #3b66bc;
  color: #fff;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
}

.weekly-view-global__th--left {
  text-align: left;
}

.weekly-view-global__td {
  padding: 0.375rem 0.625rem;
  border: 1px solid #dbe6f5;
  color: #1f2937;
  vertical-align: middle;
  background: #fff;
}

.weekly-view-global__td--activity,
.weekly-view-global__td--subactivity {
  max-width: 18rem;
}

.weekly-view-global__row:hover .weekly-view-global__td {
  background: #f8fafc;
}

.weekly-view-global__link {
  color: #3b66bc;
  font-weight: 600;
  text-decoration: none;
}

.weekly-view-global__link:hover {
  text-decoration: underline;
}

.weekly-view-global__activity-cell,
.weekly-view-global__subactivity-cell {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  min-width: 0;
}

.weekly-view-global__activity-name,
.weekly-view-global__subactivity-name {
  min-width: 0;
  flex: 1;
}

.weekly-view-global__activity-name {
  font-weight: 700;
  text-transform: uppercase;
}

.weekly-view-global__subactivity-cell {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.weekly-view-global__attachment-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  background: #eef3fb;
  color: #3b66bc;
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
}

.weekly-view-global__tag {
  display: inline-flex;
  align-items: center;
  margin: 0.125rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.125rem;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}

.weekly-view-global__td--progress {
  min-width: 7rem;
}

.weekly-view-global__executed-value {
  color: #166534;
  font-weight: 700;
}

.weekly-view-global__empty {
  padding: 1.5rem;
  text-align: center;
  color: #6b7280;
}
</style>
